import { debounce, noop } from 'lodash-es'
import { formField } from '@epages/react-components'
import { forwardRef, useEffect, useState } from 'react'
import postcss from 'postcss'

import { useTranslate } from '../../utils/translate'
import CodeEditor from './CodeEditor'

const codeMirrorOptions = {
  lineNumbers: true,
  lineWrapping: true,
  mode: 'css',
  theme: 'base16-light',
  viewportMargin: Infinity,
}

type CustomCssFieldProps = Readonly<{
  value: string
  onChange: (value: string) => void
  onInfoText: (text: string) => void
  onInput: (value: string) => void
  resetValue: string
}>

// eslint-disable-next-line react/display-name
export const CustomCssField = forwardRef<HTMLElement, CustomCssFieldProps>(
  ({ value, onChange, onInput = noop, onInfoText }, _ref) => {
    const [currentValue, setCurrentValue] = useState(value)
    const [invalid, setInvalid] = useState(false)

    const t = useTranslate('interface')

    useEffect(() => {
      onInfoText(t(`components.customCssFieldComponent.explanation`))
    }, [onInfoText, t])

    const processCss = debounce((val) => {
      try {
        postcss.parse(val, { from: undefined })
        setInvalid(false)
        onChange(val)
      } catch {
        setInvalid(true)
      }
    }, 500)

    return (
      <CodeEditor
        value={currentValue}
        resetValue=""
        invalid={invalid}
        className="ep-custom-css-field"
        warningDismissKey="customCssWarningDismissed"
        tScoped={(subKey, ...args) => t(`components.customCssFieldComponent.${subKey}`, ...args)}
        onChange={(newValue) => {
          setCurrentValue(newValue)
          onInput(newValue)
          processCss(newValue)
        }}
        codeMirrorOptions={codeMirrorOptions}
      />
    )
  },
)

export default formField()(CustomCssField)
